import { ArrowLeft } from "lucide-react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";

import { Button } from "@/components/ui/button";

import type { FormSchema } from "@/lib/ai-builder/types";

interface PreviewPanelHeaderProps {
	forms: FormSchema[];
	activeFormId: string | null;
	setActiveFormId: (id: string) => void;
	activeForm: FormSchema | undefined;
	setShowJsonModal: (show: boolean) => void;
	onUseForm: () => void;
	isMobile?: boolean;
}

export function PreviewPanelHeader({
	forms,
	activeFormId,
	setActiveFormId,
	activeForm,
	setShowJsonModal,
	onUseForm,
	isMobile = false,
}: PreviewPanelHeaderProps) {
	const t = useTranslations("aiBuilder");
	if (!forms || forms.length === 0) {
		return null;
	}

	if (isMobile) {
		return (
			<>
				<div className="flex items-center gap-3 border-b bg-card/50 p-4 backdrop-blur md:hidden">
					<Button asChild size="icon" variant="ghost">
						<Link href="/form-builder">
							<ArrowLeft className="size-4" />
						</Link>
					</Button>
					<div className="inline-flex items-center gap-2">
						<span className="size-2 rounded-2xl bg-muted-foreground" />
						<span className="font-semibold text-lg">AI</span>
					</div>
				</div>
				<div className="hidden gap-2 overflow-x-auto p-3 max-sm:flex">
					{forms.map((form, idx) => (
						<Button
							key={form.id}
							onClick={() => setActiveFormId(form.id)}
							size="sm"
							variant={form.id === activeFormId ? "secondary" : "outline"}
						>
							{form.prompt
								? `${form.prompt.slice(0, 12)}...`
								: t("formNumber", { n: idx + 1 })}
						</Button>
					))}
				</div>
				{activeForm?.schema && (
					<div className="hidden px-3 pb-3 max-sm:flex">
						<Button
							className="w-full rounded-2xl py-3 font-semibold text-base"
							onClick={onUseForm}
							size="lg"
						>
							{t("useThisForm")}
						</Button>
					</div>
				)}
			</>
		);
	}

	return (
		<div className="hidden items-center justify-between gap-4 border-b bg-card/50 p-4 md:flex">
			<div className="flex gap-2">
				{forms.map((form, idx) => (
					<Button
						key={form.id}
						onClick={() => setActiveFormId(form.id)}
						variant={form.id === activeFormId ? "secondary" : "outline"}
					>
						{form.prompt ? `${form.prompt.slice(0, 20)}...` : t("formNumber", { n: idx + 1 })}
					</Button>
				))}
			</div>
			{activeForm?.schema && (
				<div className="flex items-center gap-2">
					<Button
						onClick={() => setShowJsonModal(true)}
						size="sm"
						variant="outline"
					>
						{t("viewJson")}
					</Button>
					<Button onClick={onUseForm} size="sm">
						{t("useThisForm")}
					</Button>
				</div>
			)}
		</div>
	);
}
